import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, Spinner, Alert, Badge, Modal, Switch } from '@components/ui';
import {
  ExclamationCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  UserIcon,
  BuildingStorefrontIcon,
  IdentificationIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useInvoicesArchive } from '../../invoices/hooks/useInvoices';
import { useSelectedCompany } from '../../company/hooks/useSelectedCompany';
import { useAuthStore } from '@features/auth/store/authStore';
import { getHomeRouteForRole } from '@lib/utils/roleNavigation';
import { useInvoiceFilterStore, filterErrorInvoices } from '@/stores/invoiceFilterStore';
import {
  StatusChip,
  DocTypeBadge,
  InvoiceRowsSkeleton,
  CARD,
  FIELD,
  FIELD_LABEL,
  GHOST,
  TH,
  TD,
  FieldInfo,
  FilterGroup,
} from './InvoiceTableAtoms';
import { ROW_HOVER, INVOICE_STATUS_GROUPS } from '@components/ui/designTokens';

/**
 * Archivo histórico — mismo sistema visual Y misma lógica de filtros que
 * Facturas Emitidas (Claude Design · FacturasEmitidas.dc.html).
 *
 * Mismo dropdown de filtros flotante con borde en degrade, mismas columnas
 * de tabla (Documento con CUFE/resolución, Cliente, Tienda · Pto, Estado,
 * Monto total, Emisión), sin la franja de tarjetas con gráficos que tenía
 * arriba (se sacó a pedido). Antes usaba fichas de fila agrupadas por mes +
 * un accordion de filtros — reemplazados por los mismos componentes de
 * Facturas Emitidas (`FilterGroup`, `FieldInfo`, `TH`/`TD`, viven en
 * InvoiceTableAtoms para no duplicarlos entre las dos pantallas).
 *
 * El buscador (CUFE / N.º de documento) y el agrupamiento de Estado en 4
 * categorías funcionan IGUAL que en Facturas Emitidas — el endpoint de
 * archive (`/invoices/archive` → `searchInvoicesArchive`) no tenía ese
 * soporte al principio (mandaba `search`/`invoiceStatus` directo a Aludra sin
 * reintento ni filtrado client-side), así que se le agregó el MISMO
 * mecanismo que ya tenía `/invoices`: fetch de hasta 4 páginas de 100 +
 * filtro client-side, con el mismo tope de seguridad. Ver
 * invoice.controller.ts → `searchInvoicesArchive`.
 *
 * Lo único que sí sigue siendo propio de esta pantalla (reglas de negocio
 * reales, no limitaciones a rodear):
 *   - El corte de 90 días (`getDefaultEndDate`), con el mismo `max` en el
 *     input de Fecha Fin y su defensa espejo en el backend.
 *   - La paginación sigue visible aunque "Ocultar facturas con error" oculte
 *     todas las filas de la página actual (AgilityTask cmrdzshw002xpg2fiad9caumc).
 */

// Panama usa el Balboa, igual que en Facturas emitidas y el dashboard.
const CURRENCY = 'B/.';

/**
 * Muestra un `yyyy-MM-dd` como `dd/MM/yyyy` sin pasar por `new Date()`: esa
 * cadena se parsea como UTC y, al formatearla en hora de Panamá (UTC-5), la
 * fecha retrocede un día — la cinta mostraba un día menos que el mismo valor
 * en el input "Fecha fin".
 */
const formatIsoDay = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const money = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Aludra almacena el CUFE sin el prefijo "FE"; se agrega aquí solo para mostrarlo.
const formatCufe = (cufe?: string): string => {
  if (!cufe) return '-';
  return cufe.startsWith('FE') ? cufe : `FE${cufe}`;
};

// Mapeo de colores segun EInvoiceStatus (equivalente a los label-* de Bootstrap)
const getStatusVariant = (eInvoiceStatus: number): 'success' | 'danger' | 'warning' | 'default' => {
  switch (eInvoiceStatus) {
    case 0: return 'success';    // Procesado
    case 1: return 'danger';     // Error
    case 2: return 'warning';    // Autorizado uso de FE
    case 3: return 'warning';    // Procesando
    case 4: return 'warning';    // Pendiente
    case 5: return 'default';    // Anulada
    default: return 'default';
  }
};

// Mapeo de variante de Badge a color de texto para el icono de advertencia
const getIconColorClass = (variant: string): string => {
  switch (variant) {
    case 'info': return 'text-primary-600';
    case 'danger': return 'text-danger-600';
    case 'warning': return 'text-warning-600';
    default: return 'text-secondary-600';
  }
};

function StatusBadgeWithModal({
  status,
  eInvoiceStatus,
  responseCode,
  resolution,
  forceWaitOnEndpoints,
  testIdBase,
}: {
  status: string;
  eInvoiceStatus: number;
  responseCode?: string;
  resolution?: string;
  forceWaitOnEndpoints?: string;
  testIdBase?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const variant = getStatusVariant(eInvoiceStatus);
  const tooltipText = [responseCode, resolution].filter(Boolean).join(' - ');
  const showWarningIcon = forceWaitOnEndpoints === 'true';
  const iconColor = getIconColorClass(variant);

  return (
    <>
      <span className="inline-flex items-center gap-1" title={tooltipText || undefined}>
        {showWarningIcon && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            data-testid={testIdBase ? `${testIdBase}-warning-button` : undefined}
            className={`${iconColor} hover:opacity-70 transition-opacity`}
            title="Ver detalle del estado"
          >
            <ExclamationCircleIcon className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          data-testid={testIdBase ? `${testIdBase}-badge-button` : undefined}
        >
          <StatusChip eInvoiceStatus={eInvoiceStatus}>{status || 'Desconocido'}</StatusChip>
        </button>
      </span>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        kicker="Documento · Estado"
        title="Detalle del estado"
        icon={<InformationCircleIcon />}
        size="sm"
        data-testid={testIdBase ? `${testIdBase}-modal` : undefined}
        footer={
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsModalOpen(false)}
              data-testid={testIdBase ? `${testIdBase}-modal-close-button` : undefined}
            >
              Cerrar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={variant} size="md">
              {status || 'Desconocido'}
            </Badge>
          </div>
          {responseCode && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Código de respuesta
              </p>
              <p className="mt-1 break-words [overflow-wrap:anywhere] text-sm text-secondary-900 dark:text-white">
                {responseCode}
              </p>
            </div>
          )}
          {resolution && (
            <div className="rounded-lg bg-secondary-50 dark:bg-secondary-700 p-3">
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">
                Resolución
              </p>
              <p className="mt-1 break-words [overflow-wrap:anywhere] text-sm text-secondary-900 dark:text-white">
                {resolution}
              </p>
            </div>
          )}
          {!responseCode && !resolution && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              No hay información adicional disponible.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}

/**
 * Mapeo de tipos de documento. Mismas siglas que ya mostraba la tabla de esta
 * pantalla (distinta de las siglas de Facturas Emitidas a propósito — son los
 * mismos valores que ya se le mostraban al usuario acá, no se tocan).
 */
const docTypeLabels: Record<string, { text: string; variant: string; title: string }> = {
  '01': { text: 'FA',   variant: 'primary', title: 'Factura de Operación Interna' },
  '02': { text: 'FI',   variant: 'default', title: 'Factura de Importación' },
  '03': { text: 'FEXP', variant: 'info',    title: 'Factura de Exportación' },
  '04': { text: 'NC',   variant: 'success', title: 'Nota de Crédito Referente a una o Varias FE' },
  '05': { text: 'ND',   variant: 'warning', title: 'Nota de Débito Referente a una o Varias FE' },
  '06': { text: 'NCG',  variant: 'default', title: 'Nota de Crédito Genérica' },
  '07': { text: 'NDG',  variant: 'default', title: 'Nota de Débito Genérica' },
  '08': { text: 'FZF',  variant: 'default', title: 'Factura de Zona Franca' },
  '09': { text: 'REMB', variant: 'info',    title: 'Reembolso' },
  '10': { text: 'FOPE', variant: 'primary', title: 'Factura de operación extranjera' },
};

/**
 * Opciones del chip de filtro "Estado": los mismos 4 estados reales del
 * negocio que Facturas Emitidas (Procesada / Autorizado uso de la FE /
 * Error / Anulada — INVOICE_STATUS_GROUPS en designTokens.ts), no los 6
 * eInvoiceStatus granulares de Aludra. El value de "Autorizado uso de la FE"
 * queda como "2,3,4" — el backend (`searchInvoicesArchive`, igual que
 * `searchInvoices`) lo interpreta como set de códigos.
 */
const ESTADO_FILTER_OPTIONS: { value: string; label: string; dot: string | null }[] = [
  { value: '', label: 'Todos', dot: null },
  ...INVOICE_STATUS_GROUPS.map((g) => ({ value: g.codes.join(','), label: g.label, dot: g.bar })),
];

/** Altura unica de los controles de la fila de busqueda (input + botones). */
const CONTROL_H = 'h-11';

export function MinorPage() {
  const { user } = useAuthStore();
  const homeRoute = getHomeRouteForRole(user?.role);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  // Cursor de escaneo incremental para busquedas con filtro de texto/nombre/
  // estado (ver invoiceSearchCursor.ts en el backend, mismo mecanismo que
  // Facturas Emitidas) — undefined = primera pagina. `cursorHistory` guarda
  // los cursores visitados para que "Anterior" no tenga que re-escanear nada;
  // vive solo en memoria del componente, se resetea con cada busqueda nueva.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const cursorHistoryRef = useRef<(string | undefined)[]>([]);

  // Default date range: last 6 months. Constrains the Aludra query window so
  // high-volume companies don't timeout fetching all history since 2024-01-01.
  const getDefaultStartDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  };
  // Archivo Histórico solo debe mostrar facturas con fecha > 90 días (ver alerta debajo),
  // así que la fecha fin por defecto/máxima es "hoy - 90 días", no "hoy".
  const getDefaultEndDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  };

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  // "Mas filtros" es un dropdown flotante (position: absolute), igual que en
  // Facturas Emitidas — no empuja la tabla hacia abajo al abrirse.
  const filterPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMoreFilters) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setShowMoreFilters(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMoreFilters(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMoreFilters]);

  // Estados para los valores de los inputs (filtros locales)
  const [storeCode, setStoreCode] = useState('');
  const [billingPoint, setBillingPoint] = useState('');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [cufe, setCufe] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerNumberId, setCustomerNumberId] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [documentType, setDocumentType] = useState('');

  // Estados para los filtros aplicados (los que se envían al API)
  const [appliedFilters, setAppliedFilters] = useState({
    storeCode: '',
    billingPoint: '',
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    cufe: '',
    customerName: '',
    customerNumberId: '',
    invoiceStatus: '',
    documentType: '',
  });

  /** Aplica los filtros "borrador" a `appliedFilters` y vuelve a la página 1. */
  const applyFilters = () => {
    setAppliedFilters({
      storeCode,
      billingPoint,
      startDate,
      endDate,
      cufe,
      customerName,
      customerNumberId,
      invoiceStatus,
      documentType,
    });
    setPage(1);
    // Una busqueda nueva arranca el escaneo de Aludra desde el principio.
    setCursor(undefined);
    cursorHistoryRef.current = [];
  };

  /**
   * Búsqueda automática: cualquier cambio en un filtro la dispara solo, con
   * un debounce de 450ms (mismo criterio que Facturas Emitidas). "Buscar" y
   * Enter cancelan el debounce y aplican al instante.
   */
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    debounceRef.current = setTimeout(applyFilters, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCode, billingPoint, startDate, endDate, cufe, customerName, customerNumberId, invoiceStatus, documentType]);

  // Global filter state (persists across views)
  const { hideErrorInvoices, setHideErrorInvoices } = useInvoiceFilterStore();

  // Get selected company
  const { data: selectedCompanyData } = useSelectedCompany();

  // Extract companyId to use in the query key
  const companyId = selectedCompanyData?.company?.id;

  // Paginación y filtrado 100% server-side (electronicInvoiceSearchOnLite):
  // pasamos page/pageSize reales y TODOS los filtros al backend, que ya filtra y
  // pagina en Aludra.
  //
  // Este campo busca por CUFE O número de documento a la vez, igual que en
  // Facturas Emitidas — va como `search`, no como `cufe`: ese parametro es un
  // filtro EXACTO que Aludra aplica del lado del servidor, asi que si el
  // usuario tipeaba un numero de documento ahi, Aludra lo comparaba contra el
  // campo Cufe y no encontraba nada. `search` recorre
  // documentNumber/cufe/customerName del lado del backend (ver
  // invoice.controller.ts → searchInvoicesArchive) y por eso encuentra ambos.
  const { data: allInvoicesData, isLoading, isFetching, error, refetch } = useInvoicesArchive({
    page,
    pageSize,
    companyId,
    initDate: appliedFilters.startDate || undefined,
    endDate: appliedFilters.endDate || undefined,
    search: appliedFilters.cufe?.trim() || undefined,
    customerName: appliedFilters.customerName || undefined,
    customerNumberId: appliedFilters.customerNumberId || undefined,
    invoiceStatus: appliedFilters.invoiceStatus || undefined,
    documentType: appliedFilters.documentType || undefined,
    storeCode: appliedFilters.storeCode || undefined,
    billingPoint: appliedFilters.billingPoint || undefined,
    cursor,
  });

  // El backend (Lite) ya filtra y pagina server-side; usamos las filas y el total
  // que devuelve, sin re-filtrar ni cortar en el cliente (salvo el toggle global
  // "Ocultar facturas con error", igual que en MainPage/InvoiceListPage/DashboardPage).
  const displayedInvoices = filterErrorInvoices(allInvoicesData?.invoices ?? [], hideErrorInvoices);
  const searchApplied = !!allInvoicesData?.searchApplied;
  // Con `searchApplied` no hay total exacto (ver InvoiceListResponse) — el
  // backend escanea Aludra de forma incremental y no sabe cuantas
  // coincidencias hay sin agotar el dataset. Se pagina con
  // hasMore/nextCursor en vez de "Página X de Y" — ver footer más abajo.
  const total = searchApplied ? null : (allInvoicesData?.total ?? 0);

  const handleNextPage = () => {
    if (searchApplied) {
      if (!allInvoicesData?.nextCursor) return;
      cursorHistoryRef.current.push(cursor);
      setCursor(allInvoicesData.nextCursor);
    } else {
      setPage((p) => p + 1);
    }
  };

  const handlePrevPage = () => {
    if (searchApplied) {
      const previousCursor = cursorHistoryRef.current.pop();
      setCursor(previousCursor);
    } else {
      setPage((p) => p - 1);
    }
  };

  /** Hay algo que limpiar: algun campo con valor o un rango de fechas movido. */
  const hasActiveFilters =
    !!(cufe || customerName || customerNumberId || invoiceStatus || documentType || storeCode || billingPoint) ||
    startDate !== getDefaultStartDate() ||
    endDate !== getDefaultEndDate();

  const handleSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    applyFilters();
  };

  const handleClearFilters = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const defaultStart = getDefaultStartDate();
    const defaultEnd = getDefaultEndDate();
    setStoreCode('');
    setBillingPoint('');
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setCufe('');
    setCustomerName('');
    setCustomerNumberId('');
    setInvoiceStatus('');
    setDocumentType('');
    setAppliedFilters({
      storeCode: '',
      billingPoint: '',
      startDate: defaultStart,
      endDate: defaultEnd,
      cufe: '',
      customerName: '',
      customerNumberId: '',
      invoiceStatus: '',
      documentType: '',
    });
    setPage(1);
    setCursor(undefined);
    cursorHistoryRef.current = [];
  };

  const onEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" data-testid="home-minor-loading-spinner" />
      </div>
    );
  }

  if (error) {
    const err = error as any;
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.message || 'Error desconocido';
    return (
      <Alert variant="danger" title="Error al cargar facturas">
        <p>
          {status ? `[${status}] ` : ''}
          {message}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3" data-testid="home-minor-retry-button">
          Reintentar
        </Button>
      </Alert>
    );
  }

  const totalPages = searchApplied || total === null ? 1 : Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="font-display">
      {/* Cabecera */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
            arriba, igual que Facturas Emitidas.
          */}
          <nav className="flex items-baseline gap-2" aria-label="Breadcrumb">
            <ol className="flex items-baseline gap-2">
              <li>
                <Link
                  to={homeRoute}
                  className="text-[14px] text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white"
                  data-testid="home-minor-breadcrumb-home-link"
                >
                  Inicio
                </Link>
              </li>
              <li className="flex items-baseline gap-2">
                <span className="text-[14px] text-secondary-300">/</span>
                <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
                  Histórico
                </h1>
              </li>
            </ol>
          </nav>
          <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
            Consulta de facturas emitidas antes de los últimos 90 días.
          </p>
        </div>
      </div>

      {/*
        Aviso de los 90 días — regla de negocio, no es una "grafica", asi que
        se conserva igual. La fecha de corte es el mismo getDefaultEndDate()
        que limita el input "Fecha fin".
      */}
      <div className="relative mt-4 flex flex-wrap items-center gap-3.5 overflow-hidden rounded-xl border border-accent-500/20 bg-gradient-to-r from-highlight-400/[.14] via-accent-500/[.05] to-transparent px-4 py-3">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-highlight-400 text-[#6d4a08]">
          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="text-[13px] font-bold text-[#6d4a08] dark:text-highlight-200">
            En esta pantalla se muestran los registros mayores a 90 días.
          </span>
          <span className="ml-2 text-xs text-[#7a5311] dark:text-highlight-300">
            Corte: <span className="font-mono font-bold">{formatIsoDay(getDefaultEndDate())}</span>
          </span>
        </div>
        {/* /home/main es visible para todos los roles (ver AppLayout), así que el
            atajo no puede caer en una ruta que el usuario no pueda abrir. */}
        <Link
          to="/home/main"
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-[#a15f0a] transition-opacity hover:opacity-75 dark:text-highlight-300"
        >
          Ir a Facturas Activas
          <ArrowRightIcon className="h-3 w-3" />
        </Link>
      </div>

      {/* Filtros — mismo dropdown flotante con borde en degrade que Facturas Emitidas */}
      <div className={`${CARD} mt-4 p-5`}>
        <div ref={filterPanelRef} className="relative">
        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex items-center gap-1">
              <label htmlFor="cufe" className={FIELD_LABEL}>CUFE / N.º de documento</label>
              <FieldInfo text="Buscá una factura por su código CUFE o por el número de documento — no importa cuál de los dos escribas, este cuadro los reconoce a ambos." />
            </div>
            <div className="flex min-w-0">
              <div className="relative min-w-0 flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                <input
                  id="cufe"
                  type="text"
                  placeholder="Buscar por CUFE o número de documento…"
                  value={cufe}
                  onChange={(e) => setCufe(e.target.value)}
                  data-testid="home-minor-filter-cufe-input"
                  onKeyDown={onEnterSearch}
                  className={`${FIELD} ${CONTROL_H} rounded-r-none border-r-0 py-0 pl-11 font-mono text-[14px]`}
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                data-testid="home-minor-search-button"
                // `isFetching`, no `isLoading` — mismo motivo que en MainPage:
                // `isLoading` solo es true en la carga inicial, así que en
                // búsquedas siguientes el botón no reflejaba que seguía
                // buscando ni bloqueaba un doble click.
                disabled={isFetching}
                className={`${CONTROL_H} shrink-0 rounded-l-none rounded-r-[10px] bg-ink-850 px-5 text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isFetching ? 'Buscando…' : 'Buscar'}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowMoreFilters((v) => !v)}
            data-testid="home-minor-toggle-filters-button"
            aria-expanded={showMoreFilters}
            className={`${GHOST} ${CONTROL_H} justify-center py-0`}
          >
            <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
            {showMoreFilters ? 'Ocultar filtros' : 'Más filtros'}
            <ChevronDownIcon
              className={`h-3 w-3 text-secondary-400 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {showMoreFilters && (
          <div
            className="filter-panel-in absolute left-0 right-0 top-full z-[100] mt-2 rounded-[16px] p-[1.5px] shadow-[0_4px_10px_-2px_rgba(10,34,51,.08),0_18px_40px_-20px_rgba(10,34,51,.18)]"
            style={{ background: 'linear-gradient(135deg, #12c4b2 0%, #3a7ac7 50%, #6b239e 100%)' }}
          >
          <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto rounded-[14.5px] bg-white p-4 dark:bg-secondary-800">
            <FilterGroup
              title="Documento"
              tone="bg-teal-500/10 text-[#0d9488]"
              icon={<DocumentTextIcon className="h-3 w-3" />}
            >
              {/*
                "Número" se eliminó: quedaba duplicado con el cuadro de
                búsqueda principal (CUFE / N.º de documento), que ya busca
                por número de documento. Sin ese campo, "Tipo" y "Estado"
                entran en UNA sola fila (igual que Facturas Emitidas).
              */}
              <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-[200px_minmax(0,1fr)] md:gap-x-3">
                <div>
                  <div className="mb-1 flex items-center gap-1">
                    <label htmlFor="documentType" className={FIELD_LABEL}>Tipo</label>
                    <FieldInfo text="Qué clase de documento fiscal es: factura, nota de crédito, nota de débito, y así." />
                  </div>
                  <select
                    id="documentType"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    data-testid="home-minor-filter-document-type-select"
                    className={`${FIELD} cursor-pointer`}
                  >
                    <option value="">Todos los tipos</option>
                    <option value="01">Factura de Operación Interna</option>
                    <option value="02">Factura de Importación</option>
                    <option value="03">Factura de Exportación</option>
                    <option value="04">Nota de Crédito Referente a una o Varias FE</option>
                    <option value="05">Nota de Débito Referente a una o Varias FE</option>
                    <option value="06">Nota de Crédito Genérica</option>
                    <option value="07">Nota de Débito Genérica</option>
                    <option value="08">Factura de Zona Franca</option>
                    <option value="09">Reembolso</option>
                    <option value="10">Factura de operación extranjera</option>
                  </select>
                </div>
                <div>
                  <div className="mb-1 flex items-center gap-1">
                    <span className={FIELD_LABEL}>Estado</span>
                    <FieldInfo text="En qué situación está la factura ante la DGI. Procesada: se emitió sin problemas. Autorizado uso de la FE: todavía en trámite (pendiente, procesándose o ya autorizada). Error: algo falló al enviarla. Anulada: fue cancelada." />
                  </div>
                  <div className="inline-flex flex-wrap gap-1" data-testid="home-minor-filter-invoice-status-select">
                    {ESTADO_FILTER_OPTIONS.map((opt) => {
                      const active = invoiceStatus === opt.value;
                      return (
                        <button
                          key={opt.value || 'all'}
                          type="button"
                          onClick={() => setInvoiceStatus(opt.value)}
                          aria-pressed={active}
                          className={
                            active
                              ? 'inline-flex items-center gap-1 rounded-md border border-ink-850 bg-ink-850 px-2.5 py-1 text-[11.5px] font-semibold text-white'
                              : 'inline-flex items-center gap-1 rounded-md border border-[#ececec] bg-white px-2.5 py-1 text-[11.5px] font-medium text-secondary-600 transition-colors hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800 dark:text-secondary-300'
                          }
                        >
                          {opt.dot && <span className="h-1.5 w-1.5 rounded-full" style={{ background: opt.dot }} />}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </FilterGroup>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <FilterGroup
                title="Cliente"
                tone="bg-[#6b239e]/10 text-[#6b239e]"
                icon={<UserIcon className="h-3 w-3" />}
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-3">
                  <div className="relative">
                    <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" />
                    <input
                      type="text"
                      aria-label="Nombre del Cliente"
                      placeholder="Nombre del cliente…"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      data-testid="home-minor-filter-customer-name-input"
                      onKeyDown={onEnterSearch}
                      className={`${FIELD} pl-10 pr-9`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      <FieldInfo text="Busca las facturas emitidas a nombre de este cliente." />
                    </span>
                  </div>
                  <div className="relative">
                    <IdentificationIcon className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400" />
                    <input
                      type="text"
                      aria-label="RUC del Cliente"
                      placeholder="RUC / Cédula"
                      value={customerNumberId}
                      onChange={(e) => setCustomerNumberId(e.target.value)}
                      data-testid="home-minor-filter-customer-number-id-input"
                      onKeyDown={onEnterSearch}
                      className={`${FIELD} pl-10 pr-9 font-mono text-[14px]`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      <FieldInfo text="Busca por el RUC o número de cédula del cliente." />
                    </span>
                  </div>
                </div>
              </FilterGroup>

              <FilterGroup
                title="Ubicación · Fechas"
                tone="bg-[#3a7ac7]/[.14] text-[#2f66b0]"
                icon={<BuildingStorefrontIcon className="h-3 w-3" />}
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="mb-1 flex items-center gap-1">
                      <label htmlFor="storeCode" className={FIELD_LABEL}>Código de tienda</label>
                      <FieldInfo text="El código interno de la tienda o sucursal que emitió la factura." />
                    </div>
                    <input
                      id="storeCode"
                      type="text"
                      placeholder="001"
                      value={storeCode}
                      onChange={(e) => setStoreCode(e.target.value)}
                      data-testid="home-minor-filter-store-code-input"
                      onKeyDown={onEnterSearch}
                      className={`${FIELD} text-center font-mono`}
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-1">
                      <label htmlFor="billingPoint" className={FIELD_LABEL}>Punto de venta</label>
                      <FieldInfo text="El número de caja o punto de venta donde se generó la factura." />
                    </div>
                    <input
                      id="billingPoint"
                      type="text"
                      placeholder="001"
                      value={billingPoint}
                      onChange={(e) => setBillingPoint(e.target.value)}
                      data-testid="home-minor-filter-billing-point-input"
                      onKeyDown={onEnterSearch}
                      className={`${FIELD} text-center font-mono`}
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-1">
                      <label htmlFor="startDate" className={FIELD_LABEL}>Fecha Inicio</label>
                      <FieldInfo text="Desde qué fecha de emisión buscar." />
                    </div>
                    <input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="home-minor-filter-start-date-input"
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-1">
                      <label htmlFor="endDate" className={FIELD_LABEL}>Fecha Fin</label>
                      <FieldInfo text="Hasta qué fecha de emisión buscar — como máximo, el corte de 90 días." />
                    </div>
                    <input
                      id="endDate"
                      type="date"
                      value={endDate}
                      max={getDefaultEndDate()}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="home-minor-filter-end-date-input"
                      className={FIELD}
                    />
                  </div>
                </div>
              </FilterGroup>
            </div>
          </div>
          </div>
        )}
        </div>

        {/* Acciones */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#f4f4f4] pt-3.5 dark:border-secondary-700">
          <div className="flex items-center gap-2">
            <Switch
              checked={hideErrorInvoices}
              onChange={setHideErrorInvoices}
              data-testid="home-minor-hide-error-invoices-switch"
              label="Ocultar facturas con error"
              size="sm"
            />
            {hideErrorInvoices && allInvoicesData?.invoices && (
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                ({allInvoicesData.invoices.length - displayedInvoices.length} ocultas)
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              data-testid="home-minor-clear-filters-button"
              className="rounded px-3 py-2.5 text-[14px] font-medium text-secondary-500 underline-offset-4 transition-colors hover:text-ink-850 hover:underline dark:hover:text-white"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabla — mismas columnas que Facturas Emitidas */}
      <div className={`${CARD} mt-4 overflow-hidden`}>
        {/*
          Con `searchApplied` no hay total exacto — "no se han encontrado
          registros" solo se puede afirmar si esta es la primera pagina
          (`!cursor`), no encontro nada, Y no queda mas por escanear
          (`!hasMore`). Si hasMore es true, cae al bloque de abajo (tabla +
          "Siguiente") en vez de este dead-end.
        */}
        {(searchApplied
          ? (allInvoicesData?.invoices.length === 0 && !allInvoicesData?.hasMore && !cursor)
          : total === 0
        ) ? (
          <div className="py-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
              No se han encontrado registros
            </h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Ajusta los filtros o amplía el rango de fechas.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-[#ececec] px-5 py-4 dark:border-secondary-700">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-ink-850 dark:text-white">Resultados</span>
                  {isFetching && (
                    <span
                      aria-hidden="true"
                      className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-secondary-300 border-t-[#12c4b2]"
                    />
                  )}
                </div>
                <div className="mt-0.5 text-xs text-secondary-400">
                  {searchApplied
                    ? `${displayedInvoices.length} documentos en este bloque`
                    : `${displayedInvoices.length} de ${total} documentos`}
                </div>
              </div>
            </div>

            {/*
              displayedInvoices.length === 0 con total > 0 significa que
              "Ocultar facturas con error" ocultó TODAS las filas de esta
              página. La paginación sigue quedando abajo, fuera de este `if`,
              para que el usuario pueda avanzar de página en vez de quedar
              trabado sin ningún control visible (AgilityTask
              cmrdzshw002xpg2fiad9caumc).
            */}
            {isFetching ? (
              /*
                Mismo motivo que en Facturas Emitidas: durante una búsqueda o
                cambio de página en vuelo se muestra un esqueleto animado en
                vez de dejar la tabla anterior quieta con solo el spinner
                chico de "Resultados".
              */
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] table-fixed border-collapse text-[14px]">
                  <colgroup>
                    <col className="w-[34%]" />
                    <col className="w-[14%]" />
                    <col className="w-[9%]" />
                    <col className="w-[13%]" />
                    <col className="w-[12%]" />
                    <col className="w-[13%]" />
                    <col className="w-[5%]" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={TH}>Documento</th>
                      <th className={TH}>Cliente</th>
                      <th className={`${TH} text-center`}>Tienda · Pto</th>
                      <th className={TH}>Estado</th>
                      <th className={`${TH} text-right`}>Monto total</th>
                      <th className={TH}>Emisión</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody>
                    <InvoiceRowsSkeleton rows={pageSize} />
                  </tbody>
                </table>
              </div>
            ) : displayedInvoices.length === 0 ? (
              <div className="py-12 text-center">
                {searchApplied && allInvoicesData?.hasMore ? (
                  <>
                    <h3 className="text-sm font-medium text-secondary-900 dark:text-white">
                      Sin coincidencias en este bloque
                    </h3>
                    <p className="mx-auto mt-1 max-w-lg text-sm text-secondary-500 dark:text-secondary-400">
                      Puede haber más facturas que coincidan más adelante — probá "Siguiente" para seguir buscando.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-sm font-medium text-secondary-900 dark:text-white">
                      Todas las facturas de esta página tienen error
                    </h3>
                    <p className="mx-auto mt-1 max-w-lg text-sm text-secondary-500 dark:text-secondary-400">
                      Están ocultas por el filtro "Ocultar facturas con error". Avanza a la siguiente página o desactiva el filtro para verlas.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] table-fixed border-collapse text-[14px]">
                  <colgroup>
                    <col className="w-[34%]" />
                    <col className="w-[14%]" />
                    <col className="w-[9%]" />
                    <col className="w-[13%]" />
                    <col className="w-[12%]" />
                    <col className="w-[13%]" />
                    <col className="w-[5%]" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={TH}>Documento</th>
                      <th className={TH}>Cliente</th>
                      <th className={`${TH} text-center`}>Tienda · Pto</th>
                      <th className={TH}>Estado</th>
                      <th className={`${TH} text-right`}>Monto total</th>
                      <th className={TH}>Emisión</th>
                      <th className={TH} />
                    </tr>
                  </thead>
                  <tbody>
                    {displayedInvoices.map((invoice) => {
                      const docType = invoice.documentType
                        ? docTypeLabels[invoice.documentType] ?? {
                            text: invoice.documentType,
                            variant: 'default',
                            title: `Tipo ${invoice.documentType}`,
                          }
                        : { text: '—', variant: 'default', title: 'Sin tipo de documento' };
                      return (
                        <tr key={invoice.id} className={`${ROW_HOVER} last:[&>td]:border-b-0`}>
                          {/* Documento: número + tipo + CUFE + resolución */}
                          <td className={TD}>
                            <div className="inline-flex items-center gap-2">
                              <span className="font-mono font-bold text-ink-850 dark:text-white">
                                {invoice.documentNumber}
                              </span>
                              <DocTypeBadge variant={docType.variant} title={docType.title}>
                                {docType.text}
                              </DocTypeBadge>
                            </div>
                            <div className="break-all font-mono text-[12px] font-medium text-[#0d9488]">
                              {formatCufe(invoice.cufe)}
                            </div>
                            <div
                              className="mt-px line-clamp-2 break-words font-mono text-[11px] tracking-[0.06em] text-secondary-400"
                              title={invoice.resolution || undefined}
                            >
                              {invoice.resolution ? `Res. ${invoice.resolution}` : 'Sin resolución'}
                            </div>
                          </td>

                          {/* Cliente: nombre + RUC */}
                          <td className={`${TD} max-w-xs`}>
                            <div className="truncate font-medium text-ink-850 dark:text-white" title={invoice.customerName}>
                              {invoice.customerName}
                            </div>
                            <div className="mt-0.5 font-mono text-[12px] text-secondary-400">
                              {invoice.customerNumberId || '-'}
                            </div>
                          </td>

                          {/* Tienda · Punto de venta */}
                          <td className={`${TD} whitespace-nowrap text-center font-mono text-[13px] text-secondary-500`}>
                            <span className="font-semibold text-ink-850 dark:text-white">{invoice.storeCode || '-'}</span>
                            <span className="mx-1 text-secondary-300">·</span>
                            <span className="font-semibold text-ink-850 dark:text-white">{invoice.billingPoint || '-'}</span>
                          </td>

                          <td className={TD}>
                            <StatusBadgeWithModal
                              status={invoice.status ?? ''}
                              eInvoiceStatus={invoice.eInvoiceStatus ?? 0}
                              responseCode={invoice.responseCode}
                              resolution={invoice.resolution}
                              forceWaitOnEndpoints={invoice.forceWaitOnEndpoints}
                              testIdBase={`home-minor-status-${invoice.id}`}
                            />
                          </td>

                          <td className={`${TD} whitespace-nowrap text-right font-mono text-sm font-bold text-ink-850 dark:text-white`}>
                            {CURRENCY} {money(invoice.amountWithTax ?? 0)}
                          </td>

                          <td className={`${TD} whitespace-nowrap`}>
                            <div className="font-mono font-medium text-ink-850 dark:text-white">
                              {invoice.dateIssue && format(new Date(invoice.dateIssue), 'dd/MM/yyyy')}
                            </div>
                            <div className="mt-0.5 font-mono text-[12px] text-secondary-400">
                              {invoice.dateIssue && format(new Date(invoice.dateIssue), 'hh:mm a').toLowerCase()}
                            </div>
                          </td>

                          <td className={`${TD} whitespace-nowrap text-right`}>
                            <Link
                              to={`/pages/invoice/${invoice.id}`}
                              state={{ from: '/home/minor' }}
                              data-testid={`home-minor-view-details-link-${invoice.id}`}
                              title="Ver detalles"
                              aria-label={`Ver detalles de la factura ${invoice.documentNumber ?? ''}`}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition-colors hover:border-[#ececec] hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:border-secondary-600 dark:hover:bg-secondary-700 dark:hover:text-white"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Paginación: siempre visible cuando total > 0, aunque el filtro
                de error oculte todas las filas de la página actual. Con
                `searchApplied` no hay total exacto — se pagina con
                Anterior/Siguiente en vez de "Página X de Y" (ver InvoiceListResponse). */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#ececec] bg-secondary-50 px-5 py-4 dark:border-secondary-700 dark:bg-secondary-900">
              <div className="flex items-center gap-4 text-[14px] text-secondary-500 dark:text-secondary-400">
                {searchApplied ? (
                  <span>
                    <span className="font-mono font-semibold text-ink-850 dark:text-white">
                      {displayedInvoices.length}
                    </span>{' '}
                    resultado{displayedInvoices.length === 1 ? '' : 's'} en este bloque
                  </span>
                ) : (
                  <>
                    <span>
                      Mostrando{' '}
                      <span className="font-mono font-semibold text-ink-850 dark:text-white">
                        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total ?? 0)}
                      </span>{' '}
                      de <span className="font-mono font-semibold text-ink-850 dark:text-white">{total}</span> facturas
                    </span>
                    <span className="h-3.5 w-px bg-[#ececec] dark:bg-secondary-700" />
                    <span>
                      Página <span className="font-mono font-semibold text-ink-850 dark:text-white">{page}</span> de{' '}
                      <span className="font-mono">{totalPages}</span>
                    </span>
                  </>
                )}
              </div>
              <div className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={(searchApplied ? cursorHistoryRef.current.length === 0 : page === 1) || isFetching}
                  onClick={handlePrevPage}
                  data-testid="home-minor-pagination-prev-button"
                  className="h-8 min-w-[32px] rounded-lg border border-[#ececec] bg-white px-3 text-[14px] font-medium text-secondary-500 transition-colors hover:border-secondary-300 hover:text-ink-850 disabled:cursor-not-allowed disabled:opacity-40 dark:border-secondary-600 dark:bg-secondary-800"
                >
                  Anterior
                </button>
                {!searchApplied && (
                  <span className="inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-ink-850 bg-ink-850 px-2 text-[14px] font-medium text-white">
                    {page}
                  </span>
                )}
                <button
                  type="button"
                  disabled={(searchApplied ? !allInvoicesData?.hasMore : page >= totalPages) || isFetching}
                  onClick={handleNextPage}
                  data-testid="home-minor-pagination-next-button"
                  className="h-8 min-w-[32px] rounded-lg border border-[#ececec] bg-white px-3 text-[14px] font-medium text-secondary-500 transition-colors hover:border-secondary-300 hover:text-ink-850 disabled:cursor-not-allowed disabled:opacity-40 dark:border-secondary-600 dark:bg-secondary-800"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
